import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Breadcrumb from '@/components/Breadcrumb'
import CTABanner from '@/components/CTABanner'
import MetaTags from '@/components/MetaTags'
import SEOSchemaMarkup from '@/components/SEOSchemaMarkup'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Mail } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import sendStoreContactEmail from '@/services/http/contact/sendEmails'

const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome muito longo'),
  email: z
    .string()
    .trim()
    .email('Email inválido')
    .max(255, 'Email muito longo'),
  phone: z
    .string()
    .trim()
    .min(10, 'Telefone inválido')
    .max(20, 'Telefone muito longo'),
  subject: z
    .string()
    .trim()
    .min(3, 'Assunto deve ter pelo menos 3 caracteres')
    .max(200, 'Assunto muito longo'),
  message: z
    .string()
    .trim()
    .min(10, 'Mensagem deve ter pelo menos 10 caracteres')
    .max(1000, 'Mensagem muito longa'),
})

type ContactFormData = z.infer<typeof contactSchema>

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)

    try {
      await sendStoreContactEmail({
        name: data.name,
        email: data.email,
        phone: data.phone,
        subject: data.subject,
        message: data.message,
        form: 'contact_form',
      })

      toast({
        title: 'Mensagem enviada com sucesso!',
        description: 'Entraremos em contato em breve.',
      })

      form.reset()
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
      toast({
        title: 'Erro ao enviar mensagem',
        description: 'Por favor, tente novamente mais tarde.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const whatsappNumber = '554137950568'
  const whatsappMessage = encodeURIComponent(
    'Olá! Gostaria de saber mais sobre os fotolivros.',
  )

  return (
    <>
      <MetaTags
        title="Contato - FotoLivro Store | Fale Conosco"
        description="Entre em contato com a FotoLivro Store. Atendimento especializado via WhatsApp, e-mail ou formulário. Tire suas dúvidas sobre fotolivros personalizados."
        type="website"
        keywords="contato fotolivro, fale conosco, atendimento fotolivro, whatsapp fotolivro, email fotolivro store"
      />
      <SEOSchemaMarkup type="organization" />

      <div className="min-h-screen flex flex-col">
        <Header />
        <Breadcrumb />
        <main
          className="flex-1 bg-background"
          role="main"
          aria-label="Página de contato"
        >
          {/* Hero Section */}
          <section
            className="bg-gradient-to-br from-primary/10 to-secondary/10 py-20"
            aria-labelledby="contact-heading"
          >
            <div className="container mx-auto px-4 text-center">
              <h1
                id="contact-heading"
                className="text-4xl md:text-5xl font-bold text-foreground mb-4"
              >
                Entre em Contato
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Estamos aqui para ajudar você a criar o fotolivro perfeito. Fale
                conosco!
              </p>
            </div>
          </section>

          {/* Contact Info Cards */}
          <section
            className="py-8 bg-background"
            aria-label="Canais de contato"
          >
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <Card className="border-border/50">
                  <CardContent className="py-4 px-6 text-center">
                    <div className="inline-flex p-2 bg-secondary/10 rounded-full mb-3">
                      <svg
                        className="h-4 w-4 text-secondary"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    </div>
                    <h2 className="font-semibold text-base mb-1">WhatsApp</h2>
                    <a
                      href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-secondary hover:underline focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 rounded"
                      aria-label="Chamar no WhatsApp"
                    >
                      Chamar no WhatsApp
                    </a>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardContent className="py-4 px-6 text-center">
                    <div className="inline-flex p-2 bg-primary/10 rounded-full mb-3">
                      <Mail
                        className="h-4 w-4 text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <h2 className="font-semibold text-base mb-1">Email</h2>
                    <a
                      href="mailto:atendimento@fotolivro.store"
                      className="text-sm text-muted-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                      aria-label="Enviar email para atendimento@fotolivro.store"
                    >
                      atendimento@fotolivro.store
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-16" aria-labelledby="form-heading">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto">
                <h2
                  id="form-heading"
                  className="text-3xl font-bold mb-4 text-center"
                >
                  Envie sua Mensagem
                </h2>
                <p className="text-muted-foreground mb-8 text-center">
                  Preencha o formulário abaixo e entraremos em contato o mais
                  breve possível.
                </p>

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                    noValidate
                  >
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome Completo *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Seu nome"
                              {...field}
                              autoComplete="name"
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="seu@email.com"
                                {...field}
                                autoComplete="email"
                                aria-required="true"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Telefone *</FormLabel>
                            <FormControl>
                              <Input
                                type="tel"
                                placeholder="(11) 98765-4321"
                                {...field}
                                autoComplete="tel"
                                aria-required="true"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Assunto *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Sobre qual produto você quer falar?"
                              {...field}
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mensagem *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Conte-nos mais sobre o que você precisa..."
                              className="min-h-[150px] resize-y"
                              {...field}
                              aria-required="true"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </section>

          <aside
            className="container mx-auto px-4 py-12"
            aria-label="Chamada para produtos"
          >
            <CTABanner
              title="Explore nossos fotolivros"
              description="Descubra qual modelo é perfeito para suas memórias"
              buttonText="Ver Produtos"
              buttonLink="/#produtos"
              variant="secondary"
            />
          </aside>
        </main>
        <Footer />
      </div>
    </>
  )
}

export default Contact
